import { useApiQuery } from '../useApiQuery';
import { contactKeys } from '@/lib/queryKeys';
import { apiGet, operationsClient } from '@/lib/api';
import type {
  ContactListParams,
  ContactListResponse,
  Contact,
  ContactAcquisition,
  ContactAddress,
  ContactPaymentMethod,
  ContactSummary,
} from '@/types/contact';
import { normalizeContactAssignedTags } from '@/lib/contacts/contactTags';

export function useContacts(params: ContactListParams = {}, options?: { enabled?: boolean }) {
  return useApiQuery<ContactListResponse>({
    queryKey: contactKeys.list(params),
    // Webhook-triggered pulls (e.g. a contact edited in QuickBooks) update
    // the row server-side with no client-side signal we can hook on, so the
    // list will look stale until React Query happens to refetch. Drop
    // staleTime + poll every 30s so the UI catches up automatically.
    staleTime: 0,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      {
        const search = typeof params.search === 'string' ? params.search : undefined;
        const tags =
          Array.isArray(params.tags) && params.tags.length > 0 ? params.tags.join(',') : undefined;
        const raw = await apiGet<Record<string, unknown>>(operationsClient, '/contacts', {
          params: {
            parentContactId: params.parentContactId,
            page: params.page ?? 1,
            limit: params.limit ?? 25,
            search,
            kind: params.kind || undefined,
            contactType: params.contactType || undefined,
            status: params.status || undefined,
            customStatus: params.customStatus || undefined,
            source: params.source || undefined,
            leadSource: params.leadSource || undefined,
            membershipStatus: params.membershipStatus || undefined,
            tags,
            createdFrom: params.createdFrom,
            createdTo: params.createdTo,
            sortBy: params.sortBy,
            sortDir: params.sortDir,
          },
        });
        const items = (raw.data ?? raw.items ?? []) as Record<string, unknown>[];
        return {
          contacts: items.map(normalizeContact),
          total: Number(raw.total ?? items.length),
          page: Number(raw.page ?? params.page ?? 1),
          limit: Number(raw.limit ?? params.limit ?? 25),
        };
      }
    },
    // No catch here on purpose. Returning an empty page for a failed request
    // makes the query succeed, so it never retries and the list silently
    // reports that the tenant has no contacts. Let it fail and be retried.
    showErrorToast: false,
    enabled: options?.enabled,
  });
}

export function normalizeContactAddress(raw: Record<string, unknown>): ContactAddress {
  const street1 = raw.street1 ? String(raw.street1) : raw.street ? String(raw.street) : undefined;
  const postalCode = raw.postalCode
    ? String(raw.postalCode)
    : raw.zip
      ? String(raw.zip)
      : undefined;

  return {
    id: raw.id ? String(raw.id) : undefined,
    title: raw.title ? String(raw.title) : null,
    label: raw.label ? String(raw.label) : null,
    street1: street1 ?? null,
    street: street1,
    street2: raw.street2 ? String(raw.street2) : undefined,
    city: raw.city ? String(raw.city) : undefined,
    state: raw.state ? String(raw.state) : undefined,
    postalCode: postalCode ?? null,
    zip: postalCode,
    country: raw.country ? String(raw.country) : undefined,
    isBilling: raw.isBilling === undefined ? undefined : Boolean(raw.isBilling),
    isService: raw.isService === undefined ? undefined : Boolean(raw.isService),
    isPrimary: raw.isPrimary === undefined ? undefined : Boolean(raw.isPrimary),
    accessNotes: raw.accessNotes ? String(raw.accessNotes) : undefined,
    territory: raw.territory ? String(raw.territory) : undefined,
    taxJurisdictionId: raw.taxJurisdictionId ? String(raw.taxJurisdictionId) : null,
    // Display name only (resolved by backend, NOT-78). Never fall back to the raw
    // UUID — show "—" upstream when there's no resolved name.
    taxJurisdiction: raw.taxJurisdiction ? String(raw.taxJurisdiction) : undefined,
    isTaxExempt: raw.isTaxExempt === undefined ? undefined : Boolean(raw.isTaxExempt),
    taxExemptCertificate: raw.taxExemptCertificate ? String(raw.taxExemptCertificate) : null,
    taxExemptExpiry: raw.taxExemptExpiry ? String(raw.taxExemptExpiry) : null,
    customFields: Array.isArray(raw.customFields)
      ? (raw.customFields as ContactAddress['customFields'])
      : undefined,
    lat: typeof raw.lat === 'number' ? raw.lat : null,
    lng: typeof raw.lng === 'number' ? raw.lng : null,
    timezone: raw.timezone ? String(raw.timezone) : null,
    timezoneSource: raw.timezoneSource ? String(raw.timezoneSource) : null,
    timezoneResolvedAt: raw.timezoneResolvedAt ? String(raw.timezoneResolvedAt) : null,
  };
}

const DECLINE_STATES = ['HEALTHY', 'SOFT_DECLINED', 'HARD_DECLINED', 'INVALID'] as const;

export function normalizeContactPaymentMethod(
  raw: Record<string, unknown>,
): ContactPaymentMethod | null {
  const providerPaymentMethodId = raw.providerPaymentMethodId
    ? String(raw.providerPaymentMethodId)
    : null;
  if (!providerPaymentMethodId) return null;

  // Wave 2 — narrow the wire-format decline state to the public union. A value
  // outside it reads as HEALTHY: the badge looks the state up and would crash
  // on one it doesn't know.
  const wireDeclineState = String(raw.declineState ?? '').toUpperCase();
  const declineState = (DECLINE_STATES as readonly string[]).includes(wireDeclineState)
    ? (wireDeclineState as (typeof DECLINE_STATES)[number])
    : 'HEALTHY';

  return {
    id: raw.id ? String(raw.id) : providerPaymentMethodId,
    provider: raw.provider ? String(raw.provider) : 'STRIPE',
    providerPaymentMethodId,
    providerCustomerId: raw.providerCustomerId ? String(raw.providerCustomerId) : null,
    type: raw.type ? String(raw.type) : 'CREDIT_CARD',
    brand: raw.brand ? String(raw.brand) : null,
    last4: raw.last4 ? String(raw.last4) : null,
    expMonth: typeof raw.expMonth === 'number' ? raw.expMonth : null,
    expYear: typeof raw.expYear === 'number' ? raw.expYear : null,
    label: raw.label ? String(raw.label) : null,
    cardholderName: raw.cardholderName ? String(raw.cardholderName) : null,
    isDefault: Boolean(raw.isDefault),
    isExpired: Boolean(raw.isExpired),
    addedAt: raw.addedAt ? String(raw.addedAt) : null,
    addedBy: raw.addedBy ? String(raw.addedBy) : null,
    declineState,
    lastDeclineAt: raw.lastDeclineAt ? String(raw.lastDeclineAt) : null,
    lastDeclineCode: raw.lastDeclineCode ? String(raw.lastDeclineCode) : null,
    lastUsedAt: raw.lastUsedAt ? String(raw.lastUsedAt) : null,
  };
}

/** Normalize backend Prisma response → frontend Contact (minimal mapping) */
export function normalizeContact(c: Record<string, unknown>): Contact {
  const summaryRecord =
    c.summary && typeof c.summary === 'object' && !Array.isArray(c.summary)
      ? (c.summary as Record<string, unknown>)
      : null;
  const tags = normalizeContactAssignedTags(c.tags, c.tagIds);

  return {
    id: String(c.id ?? ''),
    contactNumber: c.contactNumber as string | undefined,
    firstName: String(c.firstName ?? ''),
    lastName: c.lastName ? String(c.lastName) : undefined,
    displayName: c.displayName ? String(c.displayName) : undefined,
    avatarUrl: c.avatarUrl ? String(c.avatarUrl) : null,
    primaryEmail: c.primaryEmail as string | null | undefined,
    emails: c.emails as Contact['emails'],
    primaryPhone: c.primaryPhone as string | null | undefined,
    phoneNumbers: c.phoneNumbers as Contact['phoneNumbers'],
    altPhone: c.altPhone as string | null | undefined,
    contactType: (c.contactType as Contact['contactType']) ?? 'RESIDENTIAL',
    companyName: c.companyName as string | null | undefined,
    title: c.title as string | null | undefined,
    webAddress: c.webAddress as string | null | undefined,
    parentContactId: c.parentContactId as string | null | undefined,
    parentContact: (c.parentContact as Contact['parentContact']) ?? null,
    childCount: typeof c.childCount === 'number' ? c.childCount : undefined,
    locationRoleCount: typeof c.locationRoleCount === 'number' ? c.locationRoleCount : undefined,
    isSiteContactOnly: typeof c.isSiteContactOnly === 'boolean' ? c.isSiteContactOnly : undefined,
    source: c.source as Contact['source'],
    leadSource: c.leadSource as Contact['leadSource'],
    leadSourceId: c.leadSourceId as string | null | undefined,
    language: c.language as string | null | undefined,
    preferredContactMethod: c.preferredContactMethod as Contact['preferredContactMethod'],
    doNotContact: c.doNotContact === undefined ? undefined : Boolean(c.doNotContact),
    smsFollowUp: c.smsFollowUp === undefined ? undefined : Boolean(c.smsFollowUp),
    emailFollowUp: c.emailFollowUp === undefined ? undefined : Boolean(c.emailFollowUp),
    communicationOptOut: c.communicationOptOut as Contact['communicationOptOut'],
    addresses: Array.isArray(c.addresses)
      ? (c.addresses as Record<string, unknown>[]).map(normalizeContactAddress)
      : undefined,
    communicationPreferences: c.communicationPreferences as Contact['communicationPreferences'],
    paymentMethods: Array.isArray(c.paymentMethods)
      ? (c.paymentMethods as Record<string, unknown>[])
          .map(normalizeContactPaymentMethod)
          .filter((method): method is ContactPaymentMethod => method !== null)
      : undefined,
    membershipId: c.membershipId as string | null | undefined,
    activeMembershipId: c.activeMembershipId as string | null | undefined,
    membershipStatus: c.membershipStatus as string | null | undefined,
    membershipPlanName: c.membershipPlanName as string | null | undefined,
    membershipActivatedAt: c.membershipActivatedAt as string | null | undefined,
    defaultPaymentMethodId: c.defaultPaymentMethodId as string | null | undefined,
    membershipHistory: c.membershipHistory as string[] | undefined,
    customerSavingsToDate: c.customerSavingsToDate as number | undefined,
    customerSavingsCurrentYear: c.customerSavingsCurrentYear as number | undefined,
    membershipValueToDate: c.membershipValueToDate as number | undefined,
    utilizationScore: c.utilizationScore as number | undefined,
    accountingSync: c.accountingSync as Contact['accountingSync'],
    notes: c.notes as string | null | undefined,
    tags,
    tagIds: tags.map((tag) => tag.tagId),
    customFields: c.customFields as Record<string, unknown> | undefined,
    validationWarnings: Array.isArray(c.validationWarnings)
      ? (c.validationWarnings as Contact['validationWarnings'])
      : undefined,
    isBlocked: Boolean(c.isBlocked ?? false),
    isDeleted: Boolean(c.isDeleted ?? false),
    assignedTo: c.assignedTo as string | null | undefined,
    customStatus: c.customStatus as string | null | undefined,
    customStatusColor: c.customStatusColor as string | null | undefined,
    summary: summaryRecord
      ? ({
          lifetimeRevenue: Number(summaryRecord.lifetimeRevenue ?? 0),
          grossOpenBalance: Number(
            summaryRecord.grossOpenBalance ?? summaryRecord.openBalance ?? 0,
          ),
          openBalance: Number(summaryRecord.openBalance ?? 0),
          paymentCreditAvailable: Number(
            summaryRecord.paymentCreditAvailable ?? summaryRecord.creditAvailable ?? 0,
          ),
          creditMemoAvailable: Number(summaryRecord.creditMemoAvailable ?? 0),
          creditAvailable: Number(summaryRecord.creditAvailable ?? 0),
          totalJobs: Number(summaryRecord.totalJobs ?? 0),
          lastServiceDate: summaryRecord.lastServiceDate
            ? String(summaryRecord.lastServiceDate)
            : null,
          openInvoiceCount: Number(summaryRecord.openInvoiceCount ?? 0),
          hasOverdueInvoice: Boolean(summaryRecord.hasOverdueInvoice),
          activeMembershipId: summaryRecord.activeMembershipId
            ? String(summaryRecord.activeMembershipId)
            : null,
          membershipPlanName: summaryRecord.membershipPlanName
            ? String(summaryRecord.membershipPlanName)
            : null,
          membershipStatus: summaryRecord.membershipStatus
            ? String(summaryRecord.membershipStatus)
            : null,
          memberSavings: Number(summaryRecord.memberSavings ?? 0),
          leadSource: summaryRecord.leadSource ? String(summaryRecord.leadSource) : null,
        } satisfies ContactSummary)
      : undefined,
    duplicatePhoneWarning: normalizeDuplicatePhoneWarning(c.duplicatePhoneWarning),
    // R2 — the read-only acquisition block. `normalizeContact` is an explicit
    // whitelist, so a field the API returns is invisible to the UI until it is
    // named here; the detail sidebar renders nothing without this line.
    acquisition: normalizeContactAcquisition(c.acquisition),
    createdBy: c.createdBy ? String(c.createdBy) : null,
    createdByName: c.createdByName ? String(c.createdByName) : null,
    createdAt: String(c.createdAt ?? new Date().toISOString()),
    updatedAt: String(c.updatedAt ?? new Date().toISOString()),
  };
}

/**
 * R2 — coerce the detail response's `acquisition` block into the FE shape.
 *
 * `undefined` (block absent) and an all-null block are different claims and
 * must stay distinguishable: the list read carries no block at all, while a
 * detail read of an unstamped contact carries one whose fields are null —
 * "not yet known", still claimable by the founding-event stamp. Only the
 * former hides the sidebar row.
 */
export function normalizeContactAcquisition(raw: unknown): ContactAcquisition | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const record = raw as Record<string, unknown>;
  const str = (value: unknown): string | null => (value == null ? null : String(value));
  return {
    channelId: str(record.channelId),
    channelName: str(record.channelName),
    channelSlug: str(record.channelSlug),
    acquiredAt: str(record.acquiredAt),
    foundingEventId: str(record.foundingEventId),
  };
}

/**
 * NOT-138 — coerce the BE's `duplicatePhoneWarning` payload (or null) into
 * the FE shape, defensively. Returns `undefined` for any malformed value so
 * callers can use a simple truthy check.
 */
function normalizeDuplicatePhoneWarning(raw: unknown): Contact['duplicatePhoneWarning'] {
  if (!raw || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const existing = Array.isArray(record.existingContactIds)
    ? record.existingContactIds.filter((value): value is string => typeof value === 'string')
    : [];
  if (existing.length === 0) return null;
  return {
    code: 'DUPLICATE_PRIMARY_PHONE',
    message: String(record.message ?? ''),
    phone: String(record.phone ?? ''),
    existingContactIds: existing,
  };
}
